/**
 * blinkRuntime — procedural blink controller.
 *
 * Driven by the `blink` graph node which emits a `__blink__` sentinel weight.
 * HeadModel calls `applyBlink()` after face-clip morph application to overlay
 * the blink on eyeBlinkLeft (ARKit 0) and eyeBlinkRight (ARKit 7).
 *
 * Config is set per-frame by the graph node via `setConfig()` — interval range,
 * close/hold/open durations. Defaults match UE's DynamicBlinkSM timing.
 */

const DEFAULTS = {
  minInterval: 2.0,
  maxInterval: 6.0,
  closeMs: 80,
  holdMs: 40,
  openMs: 100,
}

let _config = { ...DEFAULTS }
let _blinkStart = 0
let _nextBlinkAt = performance.now() + _randomDelay()

function _randomDelay() {
  return (_config.minInterval + Math.random() * (_config.maxInterval - _config.minInterval)) * 1000
}

function _blinkWeight(elapsed) {
  const { closeMs, holdMs, openMs } = _config
  if (elapsed < closeMs) return elapsed / closeMs
  if (elapsed < closeMs + holdMs) return 1.0
  const totalMs = closeMs + holdMs + openMs
  if (elapsed < totalMs) return 1.0 - (elapsed - closeMs - holdMs) / openMs
  return 0.0
}

export function setConfig(nodeData) {
  _config.minInterval = nodeData.minInterval ?? DEFAULTS.minInterval
  _config.maxInterval = nodeData.maxInterval ?? DEFAULTS.maxInterval
  _config.closeMs     = nodeData.closeMs     ?? DEFAULTS.closeMs
  _config.holdMs      = nodeData.holdMs      ?? DEFAULTS.holdMs
  _config.openMs      = nodeData.openMs      ?? DEFAULTS.openMs
}

/**
 * Current procedural-blink weight (0..1), advancing the scheduler. Shared by
 * both renderers: HeadModel applies it to morph targets (applyBlink below),
 * OGSModel folds it into the ARKit blink channels before the splat-rig
 * conversion. Call at most once per frame (it advances blink timing).
 * @param {number} gateWeight - sentinel weight from the graph (0 = off)
 * @returns {number} blink envelope * gateWeight
 */
export function currentBlinkWeight(gateWeight = 1) {
  if (gateWeight <= 0) return 0
  const now = performance.now()
  if (now >= _nextBlinkAt && !_blinkStart) _blinkStart = now
  if (!_blinkStart) return 0
  const elapsed = now - _blinkStart
  const totalMs = _config.closeMs + _config.holdMs + _config.openMs
  if (elapsed >= totalMs) {
    _blinkStart = 0
    _nextBlinkAt = now + _randomDelay()
    return 0
  }
  return _blinkWeight(elapsed) * gateWeight
}

/**
 * Apply procedural blink to morph target influences.
 * @param {Array} meshes - array of THREE.Mesh with morphTargetInfluences
 * @param {Object|null} mapping - ARKit index → GLB morph index (from AudioAnimationBuffer)
 * @param {number} weight - sentinel weight from graph (0 = off, 1 = full blink)
 */
export function applyBlink(meshes, mapping, weight) {
  const bw = currentBlinkWeight(weight)
  if (bw <= 0) return

  const BLINK_LEFT = 0
  const BLINK_RIGHT = 7

  for (const mesh of meshes) {
    const inf = mesh.morphTargetInfluences
    if (!inf) continue
    if (mapping) {
      const idxL = mapping[BLINK_LEFT]
      const idxR = mapping[BLINK_RIGHT]
      if (idxL !== -1) inf[idxL] = Math.min(1.0, (inf[idxL] || 0) + bw)
      if (idxR !== -1) inf[idxR] = Math.min(1.0, (inf[idxR] || 0) + bw)
    } else {
      if (BLINK_LEFT < inf.length) inf[BLINK_LEFT] = Math.min(1.0, (inf[BLINK_LEFT] || 0) + bw)
      if (BLINK_RIGHT < inf.length) inf[BLINK_RIGHT] = Math.min(1.0, (inf[BLINK_RIGHT] || 0) + bw)
    }
  }
}
